import { useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiX } from "react-icons/fi";

import api from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    title: "Screening criteria",
    description: "One list. Choose how important each criterion is. A candidate who misses a “Must have” is eliminated.",
    criterion: "Criterion",
    importance: "Importance · share of the score",
    placeholder: "e.g. 2 years minimum in a similar position",
    must: "Must have",
    high: "Very important",
    medium: "Important",
    low: "Bonus",
    eliminates: "Eliminates if missed",
    add: "+ Add a criterion",
    remove: "Remove criterion",
    legend: "Very important = 3 points, Important = 2, Bonus = 1. The score out of 100 is the share of points the candidate gets.",
    empty: "No criteria yet. If you leave the list empty, criteria are generated from the job ad at the first screening.",
    save: "Save",
    saving: "Saving…",
    saved: "Criteria saved",
    error: "Failed to save criteria",
  },
  fr: {
    title: "Critères de screening",
    description: "Une seule liste. Pour chaque critère, choisissez son importance. « Indispensable » écarte le candidat s'il n'est pas rempli.",
    criterion: "Critère",
    importance: "Importance · part du score",
    placeholder: "ex. 2 ans minimum sur un poste similaire",
    must: "Indispensable",
    high: "Très important",
    medium: "Important",
    low: "Bonus",
    eliminates: "Écarte si raté",
    add: "+ Ajouter un critère",
    remove: "Retirer le critère",
    legend: "Très important = 3 points, Important = 2, Bonus = 1. Le score sur 100 est la part des points obtenus.",
    empty: "Aucun critère. Si la liste reste vide, les critères sont générés à partir de l'offre au premier screening.",
    save: "Enregistrer",
    saving: "Enregistrement…",
    saved: "Critères enregistrés",
    error: "Erreur lors de l'enregistrement",
  },
};

const IMPORTANCES = ["must", "high", "medium", "low"];
const POINTS = { high: 3, medium: 2, low: 1 };

const toRows = (criteria) => (criteria || []).map((c) => ({ label: c.label, importance: c.importance }));

export default function Screening({ job, setJob }) {
  const { id } = useParams();
  const { t } = useComponentTranslation(translations);
  const [rows, setRows] = useState(toRows(job?.screening_criteria));
  const [saving, setSaving] = useState(false);

  const cleaned = rows.map((r) => ({ label: r.label.trim(), importance: r.importance })).filter((r) => r.label);
  const isDirty = JSON.stringify(cleaned) !== JSON.stringify(toRows(job?.screening_criteria));
  const totalPoints = rows.reduce((sum, r) => sum + (POINTS[r.importance] || 0), 0);

  const update = (index, patch) => setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const { ok, data } = await api.put(`/job/${id}`, { screening_criteria: cleaned });
      if (!ok) return toast.error(t("error"));
      setJob(data);
      setRows(toRows(data.screening_criteria));
      toast.success(t("saved"));
    } catch (e) {
      console.error(e);
      toast.error(t("error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto max-w-5xl space-y-5 p-6 pb-20">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
        <p className="text-sm text-gray-500 mt-1">{t("description")}</p>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
          <span>{t("criterion")}</span>
          <span>{t("importance")}</span>
        </div>

        {rows.length === 0 && <p className="px-4 py-6 text-sm text-gray-500 border-t border-gray-100">{t("empty")}</p>}

        {rows.map((row, index) => {
          const share = row.importance !== "must" && totalPoints ? Math.round((100 * POINTS[row.importance]) / totalPoints) : null;
          return (
            <div key={index} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-gray-100">
              <input
                type="text"
                value={row.label}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder={t("placeholder")}
                aria-label={t("criterion")}
                className="input flex-1 min-w-[240px]"
              />
              <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label={t("importance")}>
                {IMPORTANCES.map((importance) => {
                  const active = row.importance === importance;
                  const activeCls = importance === "must" ? "bg-red-700 text-white" : "bg-primary text-white";
                  return (
                    <button
                      key={importance}
                      type="button"
                      aria-pressed={active}
                      onClick={() => update(index, { importance })}
                      className={`px-3 h-9 text-sm font-medium border-r border-gray-200 last:border-r-0 transition-colors ${
                        active ? activeCls : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {t(importance)}
                    </button>
                  );
                })}
              </div>
              <div className="w-32 flex items-center gap-2 text-xs text-gray-500">
                {share == null ? (
                  <span className="font-semibold text-red-700">{t("eliminates")}</span>
                ) : (
                  <>
                    <span className="flex-1 h-1.5 rounded-full bg-violet-100 overflow-hidden">
                      <span className="block h-full bg-primary" style={{ width: `${share}%` }} />
                    </span>
                    {share} %
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setRows(rows.filter((_, i) => i !== index))}
                aria-label={t("remove")}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          );
        })}

        <div className="px-4 py-2 border-t border-gray-100">
          <button type="button" onClick={() => setRows([...rows, { label: "", importance: "high" }])} className="text-sm font-semibold text-primary py-2">
            {t("add")}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">{t("legend")}</p>

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={!isDirty || saving} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? t("saving") : t("save")}
        </button>
      </div>
    </div>
  );
}
